import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import {
  ActivateSupplierLevelAgreementTable,
  SupplierWithActiveAgreement,
} from '@retail/purchase-agreements/components';
import { Supplier } from '@retail/suppliers/types';
import { ContainedButton, Dialog, TextButton } from '@shared/components';
import { useCallback, useState } from 'react';
import { PurchaseAgreement } from '@retail/purchase-agreements/types';
import { getISO_8601Date } from '@retail/utils';
import { useConditionActions } from '@retail/calculus/context';
import { CreateCalculusConditions } from '@retail/calculus/types';
import { SupplierAgreements } from '@retail/purchase-agreements/containers';

interface Props {
  open: boolean;
  suppliers: Supplier[];
  onClose: () => void;
}

function ActivateSupplierLevelAgreementsDialog({ open, suppliers, onClose }: Props) {
  const t = useCalculusTFunction();

  const [supplierWithActiveAgreement, updateActiveCondition] = useState(
    suppliers.map((supplier) => {
      return { supplier: supplier, activeAgreement: undefined };
    }) as SupplierWithActiveAgreement[]
  );
  const handleUpdate = useCallback(
    (agreement: PurchaseAgreement, supplierId: number) =>
      updateActiveCondition(
        supplierWithActiveAgreement.map((supplier) => {
          if (supplier.supplier.mgSupplierId === supplierId) {
            if (supplier.activeAgreement?.id === agreement.id) {
              return { supplier: supplier.supplier, activeAgreement: undefined };
            }
            return { supplier: supplier.supplier, activeAgreement: agreement };
          }
          return supplier;
        })
      ),
    [supplierWithActiveAgreement]
  );

  const { createMultipleConditions, isLoadingPostChanges } = useConditionActions();

  const handlePostPurchaseChanges = useCallback(() => {
    const suppliersWithAgreement = supplierWithActiveAgreement.filter(
      (supplier) => !!supplier.activeAgreement
    );
    const conditions: CreateCalculusConditions[] = suppliersWithAgreement.map((agreement) => {
      return {
        content: { purchase: { agreementId: agreement.activeAgreement?.id } },
        binding: { supplier: { supplierNumber: agreement.supplier.mgSupplierId } },
        validFrom: getISO_8601Date(),
      };
    });
    createMultipleConditions(conditions);
    onClose();
  }, [createMultipleConditions, onClose, supplierWithActiveAgreement]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t('calculus.activateSupplierAgreement')}</DialogTitle>
      <DialogContent className="padding-0">
        <ActivateSupplierLevelAgreementTable
          suppliersWithActiveAgreement={supplierWithActiveAgreement}
          renderAgreements={(supplierWithActiveAgreement: SupplierWithActiveAgreement) => (
            <SupplierAgreements
              supplierWithActiveAgreement={supplierWithActiveAgreement}
              handleUpdate={handleUpdate}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <TextButton onClick={onClose} size="small" disabled={isLoadingPostChanges}>
          {t('calculus.cancel')}
        </TextButton>
        <ContainedButton
          size="small"
          onClick={handlePostPurchaseChanges}
          loading={isLoadingPostChanges}
        >
          {t('calculus.activate')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}

export default withDialogSuspenseErrorBoundary(ActivateSupplierLevelAgreementsDialog);
